// Сеточная кластеризация маркеров на карте.
// Точки, которые на экране оказываются в одной ячейке CELL_PX×CELL_PX пикселей,
// объединяются в один кружок с числом. Без внешних библиотек — O(n) на каждый пересчёт.

export type Point<T> = { lng: number; lat: number; item: T }

export type Cluster<T> = {
  key: string
  lng: number
  lat: number
  items: T[]
}

type Bounds = [[number, number], [number, number]] // [[lng, lat], [lng, lat]]

const TILE = 256
export const CELL_PX = 64
// С какого зума перестаём кластеризовать (дальше показываем точки по одной,
// а совпадающие адреса — списком)
export const MAX_CLUSTER_ZOOM = 16

// Перевод в «мировые» пиксели Web Mercator на данном зуме
const toWorldPx = (lng: number, lat: number, zoom: number): [number, number] => {
  const scale = TILE * Math.pow(2, zoom)
  const x = ((lng + 180) / 360) * scale
  const clampedLat = Math.max(-85, Math.min(85, lat))
  const sin = Math.sin((clampedLat * Math.PI) / 180)
  const y = (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)) * scale
  return [x, y]
}

const inBounds = (lng: number, lat: number, b: Bounds | undefined, padding: number) => {
  if (!b) return true
  const minLng = Math.min(b[0][0], b[1][0])
  const maxLng = Math.max(b[0][0], b[1][0])
  const minLat = Math.min(b[0][1], b[1][1])
  const maxLat = Math.max(b[0][1], b[1][1])
  const padLng = (maxLng - minLng) * padding
  const padLat = (maxLat - minLat) * padding
  return lng >= minLng - padLng && lng <= maxLng + padLng && lat >= minLat - padLat && lat <= maxLat + padLat
}

export function clusterPoints<T>(points: Point<T>[], zoom: number, bounds?: Bounds): Cluster<T>[] {
  const z = Math.max(0, Math.round(zoom))
  const cells = new Map<string, { sumLng: number; sumLat: number; items: T[] }>()

  for (const p of points) {
    if (!Number.isFinite(p.lng) || !Number.isFinite(p.lat)) continue
    // Рисуем только то, что видно (+ запас 25% по краям, чтобы не мигало при сдвиге)
    if (!inBounds(p.lng, p.lat, bounds, 0.25)) continue

    let key: string
    if (z >= MAX_CLUSTER_ZOOM) {
      // На большом зуме объединяем только точки с одинаковым адресом (~1 м)
      key = `${p.lng.toFixed(5)}:${p.lat.toFixed(5)}`
    } else {
      const [x, y] = toWorldPx(p.lng, p.lat, z)
      key = `${Math.floor(x / CELL_PX)}:${Math.floor(y / CELL_PX)}`
    }

    const cell = cells.get(key)
    if (cell) {
      cell.sumLng += p.lng
      cell.sumLat += p.lat
      cell.items.push(p.item)
    } else {
      cells.set(key, { sumLng: p.lng, sumLat: p.lat, items: [p.item] })
    }
  }

  const result: Cluster<T>[] = []
  cells.forEach((c, key) => {
    result.push({ key, lng: c.sumLng / c.items.length, lat: c.sumLat / c.items.length, items: c.items })
  })
  return result
}
